/**
 * Where everything in the hero frame sits, at any width.
 *
 * The dashboard is laid out once at FRAME_W x FRAME_H and scaled. The athlete's
 * phone is drawn at its real width (PHONE_SCREEN_W, the 390px of an iPhone) and
 * scaled into the device, so its type and spacing are the public page's own.
 *
 * Two layouts:
 *
 *   Desktop and tablet (outer width >= MOBILE_BREAKPOINT): the handoff's. The
 *   whole dashboard is visible, the phone slides in over its right side.
 *
 *   Phone: the handoff shrank the whole dashboard to a quarter of its size and
 *   drew the phone at 1.7x over it. On a 390px screen that put the phone at
 *   about 150px wide with 5px type, and left half the box empty until 4.3s in.
 *   Here the dashboard fills the box instead, at about 0.78x so its type is
 *   readable, and a camera pans across it to follow the action: search on the
 *   left, then over to "View public page", which is also where the bell and the
 *   new activity row land. The athlete's phone rises over the middle at a
 *   legible ~310px.
 *
 * Every function here is pure so the cursor maths can be tested; the cursor
 * lands on measured element positions (see offsetCentre) rather than on
 * hand-typed coordinates, so a copy change can't leave it tapping air.
 */

export const FRAME_W = 1280;
export const FRAME_H = 800;
export const MOBILE_BREAKPOINT = 600;

/* ── The screen, at a real iPhone's 390 x 844 ── */
export const PHONE_SCREEN_W = 390;
export const PHONE_SCREEN_H = 844;

/* ── The device, in frame units (handoff: 330 wide, 10px bezel) ── */
export const PHONE_W = 330;
export const PHONE_BEZEL = 10;
/** Author px to device units: the 310px screen shows a 390px page. */
export const SCREEN_K = (PHONE_W - 2 * PHONE_BEZEL) / PHONE_SCREEN_W;
/** Tall enough for the whole 844px screen: about 691. */
export const PHONE_H = 2 * PHONE_BEZEL + PHONE_SCREEN_H * SCREEN_K;
/** Where the phone sits in the desktop frame. */
export const PHONE_X = 926;
export const PHONE_Y = 18;

/** Mobile camera: how far across the frame to look, in frame units. */
export type Camera = 'left' | 'right';

export interface Point {
  x: number;
  y: number;
}

export interface HeroLayout {
  mobile: boolean;
  /** Outer box, in CSS px. */
  width: number;
  height: number;
  /** Frame units to CSS px. */
  scale: number;
  /** Frame units of dashboard visible across the box (all of it on desktop). */
  viewW: number;
  /** The phone's top-left in CSS px (its final, landed position) and its scale. */
  phone: { x: number; y: number; scale: number };
}

/** Before a real width arrives, lay out as if 1000px wide rather than 0. */
export const FALLBACK_WIDTH = 1000;

export function computeLayout(outerWidth: number): HeroLayout {
  const width = outerWidth > 0 ? outerWidth : FALLBACK_WIDTH;

  if (width >= MOBILE_BREAKPOINT) {
    const scale = width / FRAME_W;
    return {
      mobile: false,
      width,
      height: FRAME_H * scale,
      scale,
      viewW: FRAME_W,
      phone: { x: PHONE_X * scale, y: PHONE_Y * scale, scale },
    };
  }

  // The phone sets the box: as wide as it comfortably can be, with a margin
  // either side so the dashboard still reads as the thing behind it.
  const phoneW = Math.min(310, width - 40);
  const phoneScale = phoneW / PHONE_W;
  const phoneH = PHONE_H * phoneScale;
  const height = Math.round(phoneH + 28);
  const scale = height / FRAME_H;
  return {
    mobile: true,
    width,
    height,
    scale,
    viewW: width / scale,
    phone: { x: (width - phoneW) / 2, y: 14, scale: phoneScale },
  };
}

/**
 * The camera's horizontal offset, in frame units. Desktop never pans. On a
 * phone, 'left' starts at the main column (the search field and the event's
 * title), 'right' is flush with the frame's right edge (View public page, the
 * bell, the notification and the activity column).
 */
export function cameraX(layout: HeroLayout, camera: Camera): number {
  if (!layout.mobile) return 0;
  const max = Math.max(0, FRAME_W - layout.viewW);
  const SIDEBAR_W = 238;
  return camera === 'left' ? Math.min(SIDEBAR_W, max) : max;
}

/** A point in dashboard frame units, to CSS px in the outer box. */
export function frameToOuter(p: Point, layout: HeroLayout, camera: Camera): Point {
  return { x: (p.x - cameraX(layout, camera)) * layout.scale, y: p.y * layout.scale };
}

/** A point on the phone's 390px page, to CSS px in the outer box. */
export function screenToOuter(p: Point, layout: HeroLayout): Point {
  const { x, y, scale } = layout.phone;
  return {
    x: x + (PHONE_BEZEL + p.x * SCREEN_K) * scale,
    y: y + (PHONE_BEZEL + p.y * SCREEN_K) * scale,
  };
}

/** The minimum an element needs to expose for offsetCentre. */
export interface OffsetNode {
  offsetLeft: number;
  offsetTop: number;
  offsetWidth: number;
  offsetHeight: number;
  offsetParent: OffsetNode | null | Element;
  /** `data-hero-scroll-y`: how far a scroller's content has been moved up. */
  dataset?: { heroScrollY?: string };
}

/**
 * An element's centre in its root's own, untransformed coordinates.
 *
 * Walks offsetParent rather than reading getBoundingClientRect, because the
 * rect includes whatever transform is mid-flight (the phone rising in, the
 * frame's entrance tilt). Offsets are layout, so the cursor aims at where the
 * element is going to be, and the transform maths above does the rest.
 *
 * The one transform it does honour is a page "scroll": a scroller that moves
 * its content with translateY records the distance in data-hero-scroll-y,
 * and anything inside it is aimed at where the scroll will leave it.
 * Returns null if the element isn't inside the root.
 */
export function offsetCentre(el: OffsetNode | null, root: unknown): Point | null {
  if (!el) return null;
  let x = el.offsetWidth / 2;
  let y = el.offsetHeight / 2;
  let node: OffsetNode | null = el;
  let scroller: OffsetNode | null = el;
  // Scroll offsets live on any ancestor, positioned or not, so walk parents
  // for those separately from the offsetParent chain.
  while (scroller && scroller !== root) {
    const scrolled = Number(scroller.dataset?.heroScrollY);
    if (scrolled) y -= scrolled;
    scroller = (scroller as unknown as { parentElement?: OffsetNode | null }).parentElement ?? null;
  }
  while (node && node !== root) {
    x += node.offsetLeft;
    y += node.offsetTop;
    node = node.offsetParent as OffsetNode | null;
  }
  return node === root ? { x, y } : null;
}
